import { merge } from 'lodash'
import { QueryClient, QueryClientConfig } from 'react-query'

import { gunzip } from './utilities/compression'

export type SetupQueryClientContext = {
  getTenantHostUrl: () => string
  getAccessToken: () => string
  getCurrentSpaceId: () => string
  getCurrentOrganizationId: () => string
}

export const setupQueryClient = (context: SetupQueryClientContext, options?: QueryClientConfig) => {
  const defaultQueryFn = async (queryParams: any) => {
    const tenantHostUrl = context.getTenantHostUrl()
    const accessToken = context.getAccessToken()
    const currentSpaceId = context.getCurrentSpaceId()
    const currentOrganizationId = context.getCurrentOrganizationId()

    const params: RequestInit = {
      method: 'GET',
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'x-workgrid-space': currentSpaceId
      }
    }

    let url = `${tenantHostUrl}${queryParams.queryKey[0]}`
    url = url.replace(':organizationId', currentOrganizationId)
    url = url.replace(':spaceId', currentSpaceId)

    const init = merge({}, params, queryParams.queryKey[1])

    // gunzip the body, and remove the encoding headers if gzip is disabled
    if (process.env.REACT_APP_USE_WORKGRID_GZIP === 'false') {
      delete init.headers['x-workgrid-accept-encoding']
      if (init.headers['x-workgrid-content-encoding'] === 'workgrid-gzip') {
        delete init.headers['x-workgrid-content-encoding']
        init.body = JSON.stringify(gunzip(init.body))
      }
    }

    const response = await fetch(url, init)

    if (!response.ok) {
      if (response.headers.get('Content-Type')?.startsWith('application/json')) {
        const json = await response.json()
        throw { ...json, status: response.status }
      } else {
        const text = await response.text()
        throw { text, status: response.status }
      }
    }

    if (response.headers.get('Content-Type')?.startsWith('application/json')) {
      return response.json()
    }
  }

  const defaultMutationFn = async (mutationParams: any) => {
    const tenantHostUrl = context.getTenantHostUrl()
    const accessToken = context.getAccessToken()
    const currentSpaceId = context.getCurrentSpaceId()
    const currentOrganizationId = context.getCurrentOrganizationId()

    const params: RequestInit = {
      method: 'post',
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'x-workgrid-space': currentSpaceId
      }
    }

    let url = `${tenantHostUrl}${mutationParams[0]}`
    url = url.replace(':organizationId', currentOrganizationId)
    url = url.replace(':spaceId', currentSpaceId)

    const init = merge({}, params, mutationParams[1])

    // gunzip the body, and remove the encoding headers if gzip is disabled
    if (process.env.REACT_APP_USE_WORKGRID_GZIP === 'false') {
      delete init.headers['x-workgrid-accept-encoding']
      if (init.headers['x-workgrid-content-encoding'] === 'workgrid-gzip') {
        delete init.headers['x-workgrid-content-encoding']
        init.body = JSON.stringify(gunzip(init.body))
      }
    }

    const response = await fetch(url, init)

    if (!response.ok) {
      if (response.headers.get('Content-Type')?.startsWith('application/json')) {
        const json = await response.json()
        throw { ...json, status: response.status }
      } else {
        const text = await response.text()
        throw { text, status: response.status }
      }
    }

    if (response.headers.get('Content-Type')?.startsWith('application/json')) {
      return response.json()
    }
  }

  return new QueryClient(
    merge(
      {
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            queryFn: defaultQueryFn,
            cacheTime: process.env.NODE_ENV === 'test' ? 0 : undefined,
            retry: process.env.NODE_ENV === 'test' ? false : undefined
          },
          mutations: {
            mutationFn: defaultMutationFn,
            retry: process.env.NODE_ENV === 'test' ? false : undefined
          }
        }
      },
      options
    )
  )
}
